import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/ui/shadcn/button';
import { Icon } from '../../icons/Icon';
import styles from './BackLink.module.css';

export interface BackLinkProps {
    /** Where the back affordance returns to — the parent section's list path (`/accounts`). */
    readonly to: string;
    /** The place it returns to, in Title Case (`Accounts`): its accessible name is *Back to Accounts*. */
    readonly label: string;
    readonly 'data-testid'?: string;
    /**
     * The route state the navigation carries — the account form closes with `state: {}`, so the list
     * knows the user came back from it (F1-b WI-1.2 plan Decision 15). Omitted: no state, as before.
     */
    readonly state?: unknown;
    /**
     * Replace the current history entry instead of pushing one — the account form's back link after the
     * account was created, so *Back* does not return to a form already submitted. Omitted: a push.
     */
    readonly replace?: boolean;
    /**
     * Unavailable for a passing reason (a save in flight). Rendered as `aria-disabled="true"`, never the
     * bare `disabled` attribute: the link stays focusable, and a click or `Enter` on it does not navigate.
     */
    readonly 'aria-disabled'?: boolean;
    /** The id of the element that states why the link is unavailable; applied only while it is. */
    readonly 'aria-describedby'?: string | undefined;
}

/**
 * The shared §6 "Back navigation" affordance: an arrow-only button beside the page's title, so a sub-view is
 * never a dead end (F5-b spec FR-10 / AC-10). It sits in `PageHeading`'s title row, under the breadcrumb that
 * names every place above the page; the arrow returns one level up.
 *
 * Drawn as the shadcn ghost icon Button (AD #39) — `buttonVariants` on the router's `Link`, because it
 * navigates: the bare `icon/md` arrow at rest, a hover fill only on hover, in a 28px square over the 24px
 * target-size floor (design-system §9), the arrow centred in it; the breadcrumb is inset to line up with it. With no visible text, its
 * accessible name is *Back to <label>* (`aria-label`), which also tells it apart from the breadcrumb's link to
 * the same place. A `react-router-dom` `Link`, not a raw `<a href>`, so the SPA does not reload. The focus
 * ring is base.css's global `:focus-visible` rule.
 *
 * **State and the disabled case** (F1-b WI-1.2 plan Decision 15). `state` is passed to the router's
 * `Link`. While `aria-disabled` is true the link carries `aria-disabled="true"` and the given
 * `aria-describedby`, and a click — which `Enter` on a link also produces — is cancelled with
 * `preventDefault`, which both the browser and the router's `Link` respect, so it does not navigate.
 * Like `Button`, it never renders the bare `disabled` attribute and stays in the tab order; its look
 * keys off the ARIA attribute (design-system §5, the Disabled row).
 */
export function BackLink({
    to,
    label,
    'data-testid': testId,
    state,
    replace = false,
    'aria-disabled': disabled = false,
    'aria-describedby': describedBy,
}: BackLinkProps) {
    function handleClick(event: MouseEvent<HTMLAnchorElement>) {
        if (disabled) {
            event.preventDefault();
        }
    }

    return (
        <Link
            aria-describedby={disabled ? describedBy : undefined}
            aria-disabled={disabled ? true : undefined}
            aria-label={`Back to ${label}`}
            className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), styles.backLink)}
            data-testid={testId}
            onClick={handleClick}
            replace={replace}
            state={state}
            to={to}
        >
            <Icon name="back" />
        </Link>
    );
}
